import React, { useEffect, useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { db } from "../apis/fb";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import "../components/css/style.css";

const sendEmail = async (formData) => {
  try {
    const response = await fetch('/api/send-contact-form-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error('Failed to send email');
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

export default function ContactSection() {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const formRef = useRef(null);
  const barGridRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [formStartTime] = useState(Date.now());
  const [honeypot, setHoneypot] = useState("");
  
  const [formData, setFormData] = useState({
    fullName: "",
    orgName: "",
    email: "",
    phone: "",
    message: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitStatus({
      type: "",
      message: ""
    });

    // Honeypot
    if (honeypot) {
      return;
    }

    const timeTaken = Date.now() - formStartTime;

    if (timeTaken < 3000) {
      setSubmitStatus({
        type: "error",
        message: "Please wait a moment before submitting."
      });

      return;
    }

    if (!turnstileToken) {
      setSubmitStatus({
        type: "error",
        message: "Please complete the security verification."
      });

      return;
    }

    setIsSubmitting(true);

    try {
      const secureFormData = {
        ...formData,
        turnstileToken,
        website: honeypot,
        formStartedAt: formStartTime
      };

      await sendEmail(secureFormData);

      const docRef = await addDoc(
        collection(db, "contactforms"),
        {
          ...formData,
          timestamp: serverTimestamp(),
          status: "new"
        }
      );

      console.log(
        "Message saved successfully:",
        docRef.id
      );

      setSubmitStatus({
        type: "success",
        message:
          "Message sent successfully! We'll get back to you soon."
      });

      setFormData({
        fullName: "",
        orgName: "",
        email: "",
        phone: "",
        message: ""
      });

      setTurnstileToken("");

    } catch (error) {
      console.error("Error submitting form:", error);

      setSubmitStatus({
        type: "error",
        message:
          "Failed to send message. Please try again later."
      });

    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!sectionRef.current) return;

    const forceShowAllElements = () => {
      const allAnimatedElements = sectionRef.current.querySelectorAll('.scroll-animate, .reveal');
      allAnimatedElements.forEach(el => {
        el.classList.add('animate-in');
        el.classList.add('active');
      });
      
      const circles = sectionRef.current.querySelectorAll('.circle');
      circles.forEach(circle => {
        circle.style.opacity = '1';
        circle.style.transform = 'scale(1)';
      });
      
      const barGrid = sectionRef.current.querySelector('.contact-section-bar-grid');
      if (barGrid) {
        barGrid.classList.add('animate-in');
        const items = barGrid.querySelectorAll('.contact-section-bar-item');
        items.forEach((item, index) => {
          item.classList.add('animate-in');
        });
      }
    };
    
    forceShowAllElements();

    const isInViewport = (element) => {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.top <= windowHeight * 0.85 && rect.bottom >= 0;
    };

    const checkAndAnimate = () => {
      const scrollAnimateElements = sectionRef.current.querySelectorAll('.scroll-animate');
      scrollAnimateElements.forEach(el => {
        if (isInViewport(el) && !el.classList.contains('animate-in')) {
          el.classList.add('animate-in');
        }
      });

      const revealElements = sectionRef.current.querySelectorAll('.reveal');
      revealElements.forEach(el => {
        if (isInViewport(el) && !el.classList.contains('active')) {
          el.classList.add('active');
        }
      });

      const barGrid = sectionRef.current.querySelector('.contact-section-bar-grid');
      if (barGrid && isInViewport(barGrid) && !barGrid.classList.contains('animate-in')) {
        barGrid.classList.add('animate-in');
        const items = barGrid.querySelectorAll('.contact-section-bar-item');
        items.forEach((item, index) => {
          setTimeout(() => {
            item.classList.add('animate-in');
          }, index * 150);
        });
      }
    };

    checkAndAnimate();

    window.addEventListener('scroll', checkAndAnimate);
    window.addEventListener('resize', checkAndAnimate);

    return () => {
      window.removeEventListener('scroll', checkAndAnimate);
      window.removeEventListener('resize', checkAndAnimate);
    };
  }, []);

  return (
    <div className="contact-section-container" ref={sectionRef} style={{opacity: 1, visibility: 'visible', display: 'block'}}>
      <div className="contact-section-blobs">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
        <div className="blob blob-4"></div>
        <div className="blob blob-5"></div>
      </div>
      
      <div className="contact-section-inner">
        <div className="contact-section-grid">
          <div className="contact-section-image-box scroll-animate" ref={imageRef}>
            <div className="agency-visual">
              <div className="circle c1 float reveal">Website Development
                <span>Custom • Fast • Conversion Focused</span>
              </div>
              <div className="circle c2 float delay1 reveal">Mobile App Design/Development
                <span>Android & iOS Apps</span>
              </div>
              <div className="circle c3 float delay2 reveal">Complete Brand Identity
                <span>Logo • Visuals • Brand Systems</span>
              </div>
              <div className="circle c4 float delay3 reveal">Digital Marketing
                <span>SEO • Content • Social Presence</span>
              </div>
              <div className="circle c5 float delay4 reveal">Performance Marketing
                <span>Meta & Google Ads • ROI Growth</span>
              </div>
            </div>
          </div>

          <div className="contact-section-form-box scroll-animate" ref={formRef}>
            <span className="contact-section-badge reveal">Get In Touch</span>
            <h2 className="reveal">Send Us a Message</h2>
            <p className="contact-section-subtitle reveal">We'd love to hear from you. Send us your message and we'll respond as soon as possible.</p>

            {submitStatus.message && (
              <div className={`contact-section-status ${submitStatus.type} reveal`}>
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                autoComplete="off"
                tabIndex="-1"
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  opacity: 0,
                  pointerEvents: "none"
                }}
              />

              <div className="contact-section-input-group reveal">
                <label>Full Name*</label>
                <input 
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="contact-section-input-group reveal">
                <label>Org Name*</label>
                <input 
                  type="text"
                  name="orgName"
                  value={formData.orgName}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                  placeholder="Your organization name"
                />
              </div>

              <div className="contact-section-input-group reveal">
                <label>Email Address*</label>
                <input 
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                  placeholder="you@example.com"
                />
              </div>

              <div className="contact-section-input-group reveal">
                <label>Phone No*</label>
                <input 
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                  placeholder="+91 XXXXXXXXXX"
                />
              </div>

              <div className="contact-section-input-group reveal">
                <label>Your Message*</label>
                <textarea 
                  name="message"
                  placeholder="Write your message here..."
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>

              <div className="contact-section-turnstile">
                <Turnstile
                  siteKey="0x4AAAAAAEpbKlkijrWYf8i_"
                  onSuccess={(token) => {
                    setTurnstileToken(token);
                  }}
                  onExpire={() => {
                    setTurnstileToken("");
                  }}
                  onError={() => {
                    setTurnstileToken("");
                  }}
                />
              </div>

              <button 
                type="submit" 
                className={`contact-section-button reveal ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                {!isSubmitting && <span className="contact-section-button-icon">→</span>}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="contact-section-bar">
        <div className="contact-section-inner">
          <div className="contact-section-bar-grid" ref={barGridRef}>
            <a 
              href="mailto:rhythmgrowthlab@gmail.com" 
              className="contact-section-bar-item"
            >
              <div className="contact-section-icon-wrapper contact-section-primary-bg">
                <svg className="contact-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <p className="contact-section-bar-label">Email Us</p>
              <h4 className="contact-section-bar-value">rhythmgrowthlab@gmail.com</h4>
              <span className="contact-section-bar-link-hint">Click to send email →</span>
            </a>

            <a 
              href="https://wa.me/919582795505?text=Hii" 
              target="_blank" 
              rel="noopener noreferrer"
              className="contact-section-bar-item"
            >
              <div className="contact-section-icon-wrapper contact-section-secondary-bg">
                <svg className="contact-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8 10a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.574 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </div>
              <p className="contact-section-bar-label">WhatsApp</p>
              <h4 className="contact-section-bar-value">+91-9582795505</h4>
              <span className="contact-section-bar-link-hint">Click to chat on WhatsApp →</span>
            </a>

            <a 
              href="https://t.me/Rhythmgrowthlab" 
              target="_blank" 
              rel="noopener noreferrer"
              className="contact-section-bar-item"
            >
              <div className="contact-section-icon-wrapper contact-section-accent-bg">
                <svg className="contact-section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9.5" />
                  <polyline points="16 2 22 8 16 8" />
                  <line x1="10" y1="14" x2="21" y2="14" />
                  <line x1="10" y1="18" x2="18" y2="18" />
                  <line x1="3" y1="10" x2="8" y2="10" />
                </svg>
              </div>
              <p className="contact-section-bar-label">Telegram</p>
              <h4 className="contact-section-bar-value">@Rhythmgrowthlab</h4>
              <span className="contact-section-bar-link-hint">Click to message on Telegram →</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}